import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { getLessonMilestones } from '../data/progressMilestones';

const { FiChevronDown, FiChevronUp } = FiIcons;

function ProgressTrack({ progress, milestones, compact = false }) {
  return (
    <div
      aria-label={`Session progress: ${progress}%`}
      aria-valuemax="100"
      aria-valuemin="0"
      aria-valuenow={progress}
      className={`floating-progress-track ${compact ? 'compact' : ''}`}
      role="progressbar"
    >
      <motion.span
        animate={{ width: `${progress}%` }}
        className="floating-progress-fill"
        initial={false}
        transition={{ duration: 0.45, ease: 'easeOut' }}
      />

      {!compact && milestones.map((milestone) => {
        const reached = progress >= milestone.percent;

        return (
          <motion.span
            animate={{
              opacity: reached ? 1 : 0,
              scale: reached ? 1 : 0.6,
              y: reached ? 0 : 4
            }}
            aria-hidden={!reached}
            aria-label={reached ? milestone.label : undefined}
            className={`progress-milestone milestone-${milestone.percent}`}
            initial={false}
            key={milestone.percent}
            style={{ left: `${milestone.percent}%` }}
          >
            {milestone.symbol}
          </motion.span>
        );
      })}
    </div>
  );
}

function SessionProgressDock({ lesson, progress }) {
  const [expanded, setExpanded] = useState(true);
  const milestones = useMemo(
    () => getLessonMilestones(lesson),
    [lesson?.id, lesson?.subject]
  );

  return (
    <div className={`session-progress-dock ${expanded ? 'expanded' : 'collapsed'}`}>
      <button
        aria-expanded={expanded}
        aria-label={expanded
          ? 'Collapse session progress'
          : 'Expand session progress'}
        className="progress-dock-toggle"
        onClick={() => setExpanded((current) => !current)}
        type="button"
      >
        <SafeIcon icon={expanded ? FiChevronDown : FiChevronUp} />
      </button>

      {expanded ? (
        <div className="progress-dock-content">
          <div className="progress-dock-copy">
            <div>
              <strong>Session progress</strong>
              <span>
                {progress === 100
                  ? 'You worked through this lesson all the way.'
                  : 'Every question moves your understanding forward.'}
              </span>
            </div>
            <b>{progress}%</b>
          </div>
          <ProgressTrack
            milestones={milestones}
            progress={progress}
          />
        </div>
      ) : (
        <div className="progress-dock-compact">
          <span>Session</span>
          <ProgressTrack
            compact
            milestones={milestones}
            progress={progress}
          />
          <strong>{progress}%</strong>
        </div>
      )}
    </div>
  );
}

export default SessionProgressDock;